import { Subscription } from 'rxjs';
import { UtilService } from 'src/app/core/services/util/util.service';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AngularPages, SiteLocations, UrlAddressService } from 'src/app/core/services/url-address/url-address.service';
import { RavService } from 'src/app/ravs/rav.service';
import { ContentFilterBy, QueryParamsFilterPage } from './filter-box.model';
@Injectable({
    providedIn: 'root',
})
export class FilterBoxService implements OnDestroy {
    private _ravService = inject(RavService);
    private _urlAddress = inject(UrlAddressService);
    private _router = inject(Router);
    private _utils = inject(UtilService);

    public AngularPages = AngularPages;
    constructor() {
        this._contentFilterBy$ = new BehaviorSubject<ContentFilterBy>(this.getEmptyContentFilterBy());

        this.getContentFilterBy().subscribe({
            next: async (contentFilterBy: ContentFilterBy) => {
                if (this._queryParamsFilterPage$) {
                    // In Universal since we use await in recurtion it might happen after the subscription is unsubscribed
                    let queryP = await this._getQueryParamsFilterPage(contentFilterBy);
                    if (!this._queryParamsFilterPage$.closed) {
                        this.setQueryParamsFilterPage(queryP);
                    }
                } else {
                    this._queryParamsFilterPage$ = new BehaviorSubject<QueryParamsFilterPage>(this.getEmptyQueryParamsFilterPage());
                }
            },
        });
        this._urlAddress.getIsFilterPage().subscribe((isFilterPage: boolean) => {
            this._isFilterPage = isFilterPage;
            if (this._isFilterPage) {
                this.isFilterBoxOpen = false;
            }
            if (!isFilterPage) {
                this.setContentFilterBy(this.getEmptyContentFilterBy());
            }
        });
        this.getQueryParamsFilterPage().subscribe(async (queryParamsFilterPage) => {
            // Check with one of the values in "queryParamsFilterPage" exists, and then navigate
            if (Object.values(queryParamsFilterPage).some((_) => !!_)) {
                // if (this._urlAddress.activeAngularPage !== this.AngularPages.Content) {
                //     this.navigate(queryParamsFilterPage);
                // }
            }
        });
    }

    private _isFilterPage = false;
    private _subscriptions: Subscription[] = [];
    public isFilterBoxOpen = false;
    public toggleFilterBoxOpen(): void {
        this.isFilterBoxOpen = !this.isFilterBoxOpen;
    }

    ngOnDestroy(): void {
        this._utils.unsubscribe(this._subscriptions);
        if (this._contentFilterBy$) {
            this._contentFilterBy$.unsubscribe();
        }

        if (this._queryParamsFilterPage$) {
            this._queryParamsFilterPage$.unsubscribe();
        }
    }

    public closeFilterBoxOpen(): void {
        this.isFilterBoxOpen = false;
    }

    private _contentFilterBy$: BehaviorSubject<ContentFilterBy>;
    public getContentFilterBy(): Observable<ContentFilterBy> {
        return this._contentFilterBy$.asObservable();
    }
    public getContentFilterBySnapshot(): ContentFilterBy {
        return this._contentFilterBy$.value;
    }
    /**
     * A detached copy to mutate before publishing, so OnPush consumers see a new reference.
     * Callers must push the result through setContentFilterBy; cloning inside that method instead
     * would turn in-place self-pushes into reference changes landing after a view was checked (NG0100).
     */
    public cloneContentFilterBy(contentFilterBy: ContentFilterBy): ContentFilterBy {
        return {
            ...contentFilterBy,
            mediaTypeList: contentFilterBy.mediaTypeList ? { ...contentFilterBy.mediaTypeList } : undefined,
            contentTypeList: contentFilterBy.contentTypeList ? { ...contentFilterBy.contentTypeList } : undefined,
            lessonLength: contentFilterBy.lessonLength ? { ...contentFilterBy.lessonLength } : undefined,
        };
    }

    public setContentFilterBy(contentFilterBy: ContentFilterBy): void {
        // Callers may push back the very object they just mutated in place (side-filter-box does this
        // from a promise callback). Pass the reference through unchanged: cloning here would turn those
        // self-pushes into reference changes landing after the view was checked (NG0100).
        this._contentFilterBy$.next(contentFilterBy);
    }

    public getEmptyContentFilterBy(): ContentFilterBy {
        return {
            rabbi: undefined,
            category: undefined,
            mediaTypeList: {
                isVideo: false,
                isAudio: false,
                isReading: false,
            },
            contentTypeList: {
                isWomanLesson: false,
                isChildLesson: false,
                isGeneralLesson: true,
            },
            lessonLength: {
                min: undefined,
                max: undefined,
            },
        };
    }

    private _queryParamsFilterPage$!: BehaviorSubject<QueryParamsFilterPage>;
    public getQueryParamsFilterPage(): Observable<QueryParamsFilterPage> {
        return this._queryParamsFilterPage$.asObservable();
    }
    public getQueryParamsFilterPageSnapshot(): QueryParamsFilterPage {
        return this._queryParamsFilterPage$.value;
    }

    public setQueryParamsFilterPage(queryParamsFilterPage: QueryParamsFilterPage): void {
        this._queryParamsFilterPage$.next(queryParamsFilterPage);
    }

    public navigate(queryParamsFilterPage: QueryParamsFilterPage): void {
        if (!!this._urlAddress.rootAngularUrl) {
            let navPath = this._urlAddress.activeAngularPage === AngularPages.Series ? 'series' : 'filter';
            this._router.navigate([`/${this._urlAddress.rootAngularUrl}/${navPath}`], {
                queryParams: queryParamsFilterPage,
            });
        }
    }

    public getEmptyQueryParamsFilterPage(): QueryParamsFilterPage {
        return {
            catId: undefined,
            filter: undefined,
            maxLength: undefined,
            minLength: undefined,
            rabbi: undefined,
            contype: undefined,
        };
    }

    private async _getQueryParamsFilterPage(contentFilterBy: ContentFilterBy): Promise<QueryParamsFilterPage> {
        const { mediaTypeList, rabbi, category, lessonLength } = contentFilterBy;
        const catId = category ? category.id : undefined;
        const { isAudio, isVideo, isReading } = mediaTypeList ?? { isAudio: false, isVideo: false, isReading: false };
        const { isWomanLesson, isChildLesson, isGeneralLesson } = contentFilterBy.contentTypeList ?? {
            isWomanLesson: false,
            isChildLesson: false,
            isGeneralLesson: false,
        };
        const mediaFilters: string[] = [];
        if (isAudio) {
            mediaFilters.push('audio');
        }
        if (isVideo) {
            mediaFilters.push('video');
        }
        if (isReading) {
            mediaFilters.push('txt');
        }

        const contentFilters: string[] = [];
        if (isWomanLesson) {
            contentFilters.push('woman');
        }
        if (isChildLesson) {
            contentFilters.push('child');
        }
        if (isGeneralLesson) {
            contentFilters.push('general');
        }
        const filter: string | undefined = mediaFilters.length && mediaFilters.length < 3 ? mediaFilters.join(',') : undefined;
        const contype: string | undefined = contentFilters.length && contentFilters.length < 3 ? contentFilters.join(',') : undefined;

        const rabbiIds: number[] = [];
        if (rabbi) {
            rabbiIds.push(await this._ravService.getAreaIdByPublicId(rabbi.id));
        }
        const rabbiId: string | undefined = rabbiIds.length ? rabbiIds.join(',') : undefined;

        const minLength: number | undefined = lessonLength?.min;
        let maxLength: number | undefined = lessonLength?.max;

        const queryParamsFilterPage: QueryParamsFilterPage = {
            rabbi: rabbiId,
            filter,
            catId,
            minLength,
            maxLength,
            contype,
        };
        return queryParamsFilterPage;
    }

    public getContentFilterByFreeze(contentFilterBy: ContentFilterBy): ContentFilterBy {
        return JSON.parse(JSON.stringify(contentFilterBy));
    }
}
